import { get } from 'lodash';

import app from '../app';

/**
 * Первоначальное условие, при котором должен срабатывать механизм retry
 * @param  {object} response ответ сервера при ошибке
 * @return {bool} начинать обработку retry или пропустить ошибку дальше
 */
const originalRetryCondition = (response) => response.status === 503 && get(response, 'data.data.actionType') === 'need_retry_stop_on_fail';

/**
 * 400 ошибки - ошибки которые нужно пропустить
 */
const responseStatusNot400 = (response) => response.status !== 400 && response.status !== -1; // response.status === -1 - запрос прервали через timeout

// Первоначальная идея была в перехвате только 503 ошибок, в процессе требование расширили до перехвата всех ошибок, исключение geocode
// Метод retryCondition возвращает true, кроме geo, намеренно. Так происходит перехват всех ошибок.
const retryMatcher = [{
  shortCode: 'geocode',
  statsKey: 'billing.api-geo',
  retryCondition: originalRetryCondition,
  isMatch: ({url}) => url.indexOf('addform/v2/geocode') !== -1
}, {
  shortCode: 'publishTerms',
  statsKey: 'billing.api-publish-terms',
  retryCondition: responseStatusNot400,
  isMatch: ({url}) => url.indexOf('/publish-terms/') !== -1
}, {
  shortCode: 'checkService',
  retryCondition: responseStatusNot400,
  isMatch: ({url}) => url.indexOf('/v1/check-service/') !== -1
}, {
  shortCode: 'checkPublish',
  statsKey: 'billing.api-check-service',
  retryCondition: responseStatusNot400,
  isMatch: ({url, method}) => (url.indexOf('/v1/check-publish/') !== -1 && method === 'POST'),
  returnOnFail: (res, $q) => {
    Object.assign(res, {
      rejectedFromRetry: true
    });
    return $q.reject(res);
  }
}];

const statsKeys = retryMatcher.reduce((acc, item) => {
  acc[item.shortCode] = item.statsKey;
  return acc;
}, {});

/*
Задача: обрабатывать ответы с 503 ошибкой и actionType = 'need_retry_stop_on_fail'

Основная идея: не резолвить API запрос, который вернул 503 ошибку - исходный код,
который вызвал API запрос, вернувший 503, никогда не увидит 503 ошибку - она
будет обработана в Interceptor.

Исключение API запрос check-publish - для него введено свойство returnOnFail,
эта функция будет вызвана после трех повторов запроса, если на все попытки -
ответ 503, то будет выполнена эта функция, в данному случае будет reject.
Это исключение, в остальных случаях Interceptor не отпускает промис, и в конечном
счете должен его зарезолвить.

Если запрос возвращает 503 ошибку, мы перехватываем запрос и повторяем этот же
самый запрос три раза. После трех попыток, показываем уведомление(заглушку) -
посылаем event retryService.events.requestFailed, его ловит директива
retry-notice с соответствующим shortCode и показывает уведомление.

Пользователь может отправить повторный запрос(четвертый и все последующие)
двумя способами:
1. Нажать кнопку "Повторить еще раз" из заглушки - отправится event
retryService.events.requestRepeat, его ловит retryInterceptor и если shortCode
совпадают, то повторяет перехваченный запрос. На время запроса кнопка отключается.

2. Нажать кнопку "Повторить еще раз" из футера формы, где кнопки сабмита.
В этом случае будут повторены все ожидающие запросы. Они хранятся в retryService
массив retryFailArr.

Для тестирования введен retryMockInterceptor
Он закомментирован в app/scripts/app.js
Строка после $httpProvider.interceptors.push('retryInterceptor');
Этот interceptor проверяет все исходящие запросы, и если запрос соотвествует
одному из retryMatcher то принудительно генерирует 503 ошибку, и если ошибок
больше 5, прекращает генерацию 503 ошибки и отдает реальный ответ.

Так как все запросы повторяются с одним конфигом, то мы через него передаем
переменную retryCount, по которой и прекращается принудительная генерация 503 ошибок,
но есть метод check-publish. Мы его реджектим после трех неудачных повторов, поэтому
конфиг и запрос возвращается и для нас теряется, поэтому с таким методом тестирования
check-publish никогда не отдаст положительный результат.

*/
app.factory('retryInterceptor', ['$q', '$injector', '$rootScope', 'retryService', 'StatsService', function($q, $injector, $rootScope, retryService, StatsService) {
  return {
    response: res => {
      if (res.config.retryCount) {
        StatsService.sendMetric(statsKeys[res.config.retryShortCode], 'number-of-retries', res.config.retryCount, 'counter');
        $rootScope.$emit(retryService.events.requestSuccessful, res.config.retryShortCode);
      }
      return res;
    },
    responseError: res => {
      if (retryMatcher.some(({retryCondition, isMatch}) => angular.isFunction(retryCondition) && retryCondition(res) && isMatch(res.config))) {
        const config = angular.copy(res.config);

        if (config && !config.retryShortCode) {
          const retryData = retryMatcher.find(item => item.isMatch(config));
          if (!retryData) {
            return $q.reject(res);
          } else {
            config.retryShortCode = retryData.shortCode;
            config.retryReturnOnFail = retryData.returnOnFail;
            config.retryCount = 0;
          }
        }

        StatsService.sendMetric(statsKeys[config.retryShortCode], 'unavailable', 1, 'counter');

        const $http = $injector.get('$http');
        config.retryCount += 1;

        if (config.retryCount >= 3) {
          $rootScope.$emit(retryService.events.requestFailed, config.retryShortCode);

          if (angular.isFunction(config.retryReturnOnFail)) {
            StatsService.sendMetric(statsKeys[config.retryShortCode], 'skip', 1, 'counter');
            return config.retryReturnOnFail(res, $q);
          }

          return $q((resolve, reject) => {
            const waitForIt = $rootScope.$on(retryService.events.requestRepeat, function(event, shortCode) {
              if (shortCode === config.retryShortCode) {
                StatsService.sendMetric(statsKeys[shortCode], 'retry', 1, 'counter');
                $rootScope.$emit(retryService.events.requestRepeatAlreadyInProgress, shortCode);
                resolve($http(config));
                waitForIt();
              }
            });
          });
        }

        return $http(config);
      }

      return $q.reject(res);
    }
  };
}]);

app.factory('retryMockInterceptor', ['$q', '$injector', function($q, $injector) {
  return {
    response: res => {
      if (res && res.config && retryMatcher.some(item => item.isMatch(res.config))) {
        if (res.config.retryCount >= 5) {
          return res;
        }
        return $q.reject(Object.assign({}, res, {
          status: 503,
          data: {
            status: 'unavailable',
            data: {
              actionType: 'need_retry_stop_on_fail'
            }
          }
        }));
      }

      return res;
    }
  };
}]);
